import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { checkRequest } from "core/utils/rsya";

const FINISHED = "FINISHED";

// Эфир: канал всегда, трансляция пока не закончилась. Закончившаяся
// трансляция играет запись, у неё конечная длительность.
const isLiveContent = (content) =>
  content?.type === "CHANNEL" ||
  (content?.type === "STREAM" && content?.streamData?.status !== FINISHED);

// Длительность записи трансляции: сумма известных длительностей записей
const recordsDuration = (records) => {
  const total = (records || [])
    .map((record) => record?.duration)
    .filter((value) => Number.isFinite(value) && value > 0)
    .reduce((sum, value) => sum + value, 0);

  return total > 0 ? total : undefined;
};

// РСЯ ограничивает интервал между запросами рекламы для всей страницы
// (одно окно или iframe = одна страница), а Player может перемонтироваться
// при смене ролика, поэтому момент последнего запроса живёт на уровне модуля.
let lastRequestAt = null;

export const __resetRsyaForTests = () => {
  lastRequestAt = null;
};

const noop = () => {};

const defaultValue = {
  content: { duration: undefined, isLive: false, url: undefined },
  check: () => null,
  markRequest: noop,
  markImpression: noop,
  adStarted: false,
  setAdStarted: noop,
};

const PlayerRsyaContext = createContext(defaultValue);

export function usePlayerRsya() {
  return useContext(PlayerRsyaContext);
}

export function PlayerRsyaProvider({ content, records, children }) {
  const impressionsRef = useRef(0);
  const contentIdRef = useRef(content?.id);
  const [adStarted, setAdStarted] = useState(false);

  // Счётчик показов относится к конкретному ролику: при смене обнуляем.
  if (contentIdRef.current !== content?.id) {
    contentIdRef.current = content?.id;
    impressionsRef.current = 0;
  }

  const isLive = isLiveContent(content);
  const duration =
    content?.type === "STREAM" && !isLive
      ? recordsDuration(records)
      : content?.duration;
  const url = content?.shareUrl;

  const check = useCallback(
    ({ width, height, now = Date.now() }) =>
      checkRequest({
        width,
        height,
        duration,
        isLive,
        now,
        lastRequestAt,
        impressions: impressionsRef.current,
        userInitiated: false,
      }),
    [duration, isLive]
  );

  // userInitiated пока не используется: в Player нет сигнала "пользователь
  // сам выбрал другой ролик", поэтому интервал не сбрасывается.
  // eslint-disable-next-line no-unused-vars
  const markRequest = useCallback(({ userInitiated } = {}) => {
    lastRequestAt = Date.now();
  }, []);

  const markImpression = useCallback(() => {
    impressionsRef.current += 1;
  }, []);

  const value = useMemo(
    () => ({
      content: { duration, isLive, url },
      check,
      markRequest,
      markImpression,
      adStarted,
      setAdStarted,
    }),
    [duration, isLive, url, check, markRequest, markImpression, adStarted]
  );

  return (
    <PlayerRsyaContext.Provider value={value}>
      {children}
    </PlayerRsyaContext.Provider>
  );
}

PlayerRsyaProvider.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    duration: PropTypes.number,
    shareUrl: PropTypes.string,
    streamData: PropTypes.shape({ status: PropTypes.string }),
  }),
  records: PropTypes.arrayOf(PropTypes.shape({ duration: PropTypes.number })),
  children: PropTypes.node,
};

export default PlayerRsyaContext;
